import {first, isArray, isEqual, isNil, last} from "lodash";
import PropTypes from "prop-types";
import PureSlider, {
    createSliderWithTooltip,
    Range as PureRange,
} from "rc-slider";
import React from "react";
import {safeAdd, safeModulo, safeSubtract} from "../../../utils/Utils";
import {Browser} from "../browser-detection/Browser";
import Button from "../button/Button";
import "./_slider.scss";

// ToDo - zaktualizować do najnowszej wersji rc-slider i przepisać komponent
const RCSlider = createSliderWithTooltip(PureSlider);
const RCRange = createSliderWithTooltip(PureRange);

const SideType = {
    LEFT: 0,
    RIGHT: 1,
};

const ChangeRate = {
    SLOW: 200,
    MEDIUM: 100,
    FAST: 50,
};

const DEBUG = false;

class Slider extends React.Component {
    holdStart = null;
    timeout = null;
    type = null;
    initialY = null;

    constructor(props) {
        super(props);
        this.state = {
            value: this.getValue(props),
        };
    }

    checkSingleValue = (v, props) => {
        const oldVal = v;
        const {min, max, step} = props;
        DEBUG && console.table({v, min, max, step});
        let tmp;
        if (isNil(v)) {
            tmp = min;
        } else {
            if (safeModulo(v, step) !== 0) {
                DEBUG && console.log("safeModulo", safeModulo(v, step));
                v = safeSubtract(v, safeModulo(v, step));
                DEBUG && console.log("safeSubtract", v);
            }
            tmp = Math.min(Math.max(v, min), max);
        }
        if (!isEqual(oldVal, tmp)) {
            console.warn(
                `incorrect value passed (step: ${step}, old: ${oldVal}, new: ${tmp})`
            );
        }
        return tmp;
    };

    getValue = (props) => {
        const {isRange, value} = props;
        let newValue;
        if (isRange) {
            newValue = [
                this.checkSingleValue(value ? value[0] : null, props),
                this.checkSingleValue(value ? value[1] : null, props),
            ];
        } else {
            newValue = this.checkSingleValue(value, props);
        }
        return newValue;
    };

    componentDidMount() {
        const newValue = this.getValue(this.props);
        if (!isEqual(this.props.value, newValue)) {
            this.onChange(newValue);
            this.onAfterChange(newValue);
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            (!isEqual(this.props.value, prevProps.value) &&
                !isEqual(this.props.value, this.state.value)) ||
            prevProps.min !== this.props.min ||
            prevProps.max !== this.props.max ||
            prevProps.step !== this.props.step
        ) {
            const newValue = this.getValue(this.props);
            this.onChange(newValue);
            this.onAfterChange(newValue);
        }
    }

    onChange = (newValue) => {
        const {
            state: {value},
            props: {overflow},
        } = this;
        if (
            !isEqual(newValue, value) &&
            (!overflow && Array.isArray(newValue)
                ? last(newValue) !== first(newValue)
                : overflow)
        ) {
            this.setState({
                value: newValue,
            });
        }
    };

    onAfterChange = (newValue) => {
        const {
            props: {onChange, value},
        } = this;
        if (!isEqual(newValue, value)) {
            onChange(newValue);
        }
    };

    shouldComponentUpdate(nextProps, nextState, nextContext) {
        return (
            !isEqual(this.props, nextProps) || !isEqual(this.state, nextState)
        );
    }

    changeValueByButton = (side, mathFunction) => {
        const {value} = this.state;
        const {step, onChange, max, min, overflow} = this.props;
        if (isArray(value)) {
            const newValue = value.slice(0);
            newValue[+(side !== SideType.LEFT)] = mathFunction(
                newValue[+(side !== SideType.LEFT)],
                step
            );
            newValue.sort((o1, o2) => o1 - o2);
            if (
                last(newValue) <= max &&
                first(newValue) <= max &&
                first(newValue) >= min &&
                last(newValue) >= min &&
                !isEqual(newValue, value) &&
                (!overflow ? last(newValue) !== first(newValue) : overflow)
            ) {
                this.setState(
                    {
                        value: newValue,
                    },
                    () => onChange(newValue)
                );
            } else {
                this.onTouchEnd();
            }
        } else {
            let newValue = mathFunction(value, step);
            if (
                newValue <= max &&
                newValue >= min &&
                !isEqual(newValue, value)
            ) {
                this.setState(
                    {
                        value: newValue,
                    },
                    () => onChange(newValue)
                );
            } else {
                this.onTouchEnd();
            }
        }
    };

    handleButtonClickRight = (event, side) => {
        event.persist();
        this.changeValueByButton(side, safeAdd);
    };

    handleButtonClickLeft = (event, side) => {
        event.persist();
        this.changeValueByButton(side, safeSubtract);
    };

    getTimeoutTime() {
        let currentTime = +new Date();
        if (currentTime - this.holdStart < 1000 * 2) return ChangeRate.SLOW;
        if (currentTime - this.holdStart < 1000 * 4) return ChangeRate.MEDIUM;
        return ChangeRate.FAST;
    }

    startTimeout(event, type) {
        this.timeout = setTimeout(() => {
            if (this.type === "+") {
                this.handleButtonClickRight(event, type);
            } else if (this.type === "-") {
                this.handleButtonClickLeft(event, type);
            }
            this.startTimeout(event, type);
        }, this.getTimeoutTime());
    }

    /**
     * prevents safari from selecting nearby text or elements
     * when holding down the +/- buttons.
     * @param value {""|"none"}
     * @private
     */
    _setGlobalTextSelection(value) {
        document.body.style.userSelect = value;
        document.body.style.webkitUserSelect = value;
    }

    onTouchStart(event, type) {
        if (Browser.is.Safari()) {
            this._setGlobalTextSelection("none");
        }
        this.holdStart = +new Date();
        this.startTimeout(event, type);
    }

    onIncrementTouchStart = (event, type) => {
        this.type = "+";
        this.onTouchStart(event, type);
    };

    onDecrementTouchStart = (event, type) => {
        this.type = "-";
        this.onTouchStart(event, type);
    };

    onTouchEnd = () => {
        if (Browser.is.Safari()) {
            this._setGlobalTextSelection("");
        }
        this.holdStart = null;
        this.type = null;
        clearTimeout(this.timeout);
    };

    disableContextMenu = (e) => {
        e.preventDefault();
        return false;
    };

    renderButtons = (props) => {
        const {isRange, disabled, max, min, reverse} = props;
        const {value} = this.state;
        let leftSide = (
            <div className="button-container">
                <Button
                    className="left"
                    buttonStyle="bordered"
                    icon={<i className="fas fa-minus" />}
                    onClick={(e) =>
                        this.handleButtonClickLeft(e, SideType.LEFT)
                    }
                    disabled={disabled || value <= min}
                    onTouchStart={(e) =>
                        this.onDecrementTouchStart(e, SideType.LEFT)
                    }
                    onTouchEnd={this.onTouchEnd}
                    onContextMenu={this.disableContextMenu}
                    onMouseDown={(e) =>
                        this.onDecrementTouchStart(e, SideType.LEFT)
                    }
                    onMouseUp={this.onTouchEnd}
                    onMouseLeave={this.onTouchEnd}
                    type="button"
                />
            </div>
        );

        let rightSide = (
            <div className="button-container">
                <Button
                    className="right"
                    buttonStyle="bordered"
                    icon={<i className="fas fa-plus" />}
                    onClick={(e) =>
                        this.handleButtonClickRight(e, SideType.RIGHT)
                    }
                    disabled={disabled || value >= max}
                    onTouchStart={(e) =>
                        this.onIncrementTouchStart(e, SideType.RIGHT)
                    }
                    onTouchEnd={this.onTouchEnd}
                    onContextMenu={this.disableContextMenu}
                    onMouseUp={this.onTouchEnd}
                    onMouseLeave={this.onTouchEnd}
                    onMouseDown={(e) =>
                        this.onIncrementTouchStart(e, SideType.RIGHT)
                    }
                    type="button"
                />
            </div>
        );

        let leftSideRange = (
            <div className="button-container range">
                <Button
                    className="right"
                    buttonStyle="bordered"
                    icon={<i className="fas fa-plus" />}
                    onClick={(e) =>
                        this.handleButtonClickRight(e, SideType.LEFT)
                    }
                    disabled={disabled || value[0] >= max}
                    onTouchStart={(e) =>
                        this.onIncrementTouchStart(e, SideType.LEFT)
                    }
                    onTouchEnd={this.onTouchEnd}
                    onContextMenu={this.disableContextMenu}
                    onMouseDown={(e) =>
                        this.onIncrementTouchStart(e, SideType.LEFT)
                    }
                    onMouseLeave={this.onTouchEnd}
                    onMouseUp={this.onTouchEnd}
                    type="button"
                />
                <Button
                    className="left"
                    buttonStyle="bordered"
                    icon={<i className="fas fa-minus" />}
                    onClick={(e) =>
                        this.handleButtonClickLeft(e, SideType.LEFT)
                    }
                    disabled={disabled || value[0] <= min}
                    onTouchStart={(e) =>
                        this.onDecrementTouchStart(e, SideType.LEFT)
                    }
                    onTouchEnd={this.onTouchEnd}
                    onContextMenu={this.disableContextMenu}
                    onMouseDown={(e) =>
                        this.onDecrementTouchStart(e, SideType.LEFT)
                    }
                    onMouseUp={this.onTouchEnd}
                    onMouseLeave={this.onTouchEnd}
                    type="button"
                />
            </div>
        );

        let rightSideRange = (
            <div className="button-container range">
                <Button
                    className="right"
                    buttonStyle="bordered"
                    icon={<i className="fas fa-plus" />}
                    onClick={(e) =>
                        this.handleButtonClickRight(e, SideType.RIGHT)
                    }
                    disabled={disabled || value[1] >= max}
                    onTouchStart={(e) =>
                        this.onIncrementTouchStart(e, SideType.RIGHT)
                    }
                    onTouchEnd={this.onTouchEnd}
                    onContextMenu={this.disableContextMenu}
                    onMouseDown={(e) =>
                        this.onIncrementTouchStart(e, SideType.RIGHT)
                    }
                    onMouseUp={this.onTouchEnd}
                    onMouseLeave={this.onTouchEnd}
                    type="button"
                />
                <Button
                    className="left"
                    buttonStyle="bordered"
                    icon={<i className="fas fa-minus" />}
                    onClick={(e) =>
                        this.handleButtonClickLeft(e, SideType.RIGHT)
                    }
                    disabled={disabled || value[1] <= min}
                    onTouchStart={(e) =>
                        this.onDecrementTouchStart(e, SideType.RIGHT)
                    }
                    onTouchEnd={this.onTouchEnd}
                    onContextMenu={this.disableContextMenu}
                    onMouseDown={(e) =>
                        this.onDecrementTouchStart(e, SideType.RIGHT)
                    }
                    onMouseUp={this.onTouchEnd}
                    onMouseLeave={this.onTouchEnd}
                    type="button"
                />
            </div>
        );

        if (!!isRange) return [leftSideRange, rightSideRange];
        return reverse ? [rightSide, leftSide] : [leftSide, rightSide];
    };

    render() {
        const {
            disabled,
            isRange,
            valueFormatter,
            tipFormatter,
            id,
            error,
            startPoint,
            reverse,
        } = this.props;
        const {value} = this.state;
        const buttons = this.renderButtons(this.props);
        //         v        - poprawka prezycji sliderów gdy mamy floaty zeby ustawiać krancowe przedziały
        let {max, min, step = 1} = this.props;
        if (!Number.isInteger(step)) {
            max = max && max + step / 100;
            min = min && min - step / 100;
        }
        //         ^        - koniec poprawki
        let className = "fetura-slider";
        if (error) className += " error position-relative";
        return (
            <div id={id} className={className} data-tip={error ? error : null}>
                {buttons[0]}
                {!!isRange && (
                    <RCRange
                        onChange={this.onChange}
                        onAfterChange={this.onAfterChange}
                        disabled={disabled}
                        min={min}
                        max={max}
                        value={value}
                        tipFormatter={tipFormatter}
                        step={step}
                    />
                )}
                {!isRange && (
                    <RCSlider
                        onChange={this.onChange}
                        onAfterChange={this.onAfterChange}
                        disabled={disabled}
                        startPoint={startPoint}
                        min={min}
                        max={max}
                        reverse={reverse}
                        value={value}
                        tipFormatter={valueFormatter}
                        step={step}
                    />
                )}
                {buttons[1]}
            </div>
        );
    }
}

Slider.propTypes = {
    onChange: PropTypes.func,
    id: PropTypes.string,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    step: PropTypes.number,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.array]).isRequired,
    isRange: PropTypes.bool,
    disabled: PropTypes.bool,
    valueFormatter: PropTypes.func,
    tipFormatter: PropTypes.func,
};

Slider.defaultProps = {
    onChange: (value) => {},
    // id: myID(),
    disabled: false,
    step: 1,
    min: 0,
    max: 100,
    isRange: false,
    valueFormatter: (value) => value,
    tipFormatter: (value) => value,
};

export default Slider;
